import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { fetchStartPageConfig } from 'tradera-state/views/start-page/actions';
import {
    selectStartPageStatus,
    selectStartPageHeader
} from 'tradera-state/views/start-page/selectors';
import { useScrollToTopOnMount } from 'tradera-hooks/use-scroll-to-top-on-mount';
import { usePageMeta } from 'tradera-hooks/use-page-meta';
import useIsDesktop from 'tradera-hooks/use-is-desktop';
import useIsMobile from 'tradera-hooks/use-is-mobile';
import useIsLoggedIn from 'tradera-hooks/use-is-loggedin';
import { useIsFeatureEnabled } from 'tradera-hooks/use-is-feature-enabled';
import Ad from 'tradera-components/ads/ad';
import ApplicationErrorBoundary from 'tradera-components/application-error-boundary/application-error-boundary';
import Loader from 'tradera-components/loader/loader';
import StartPageHero from '../components/start-page/hero/start-page-hero';
import PageContent from '../components/start-page/page-content';
import '../components/start-page/ads/start-page-ad.scss';
import './start-page.scss';

import QuickView from 'tradera-components/quick-view/quick-view-modal';
import RecentSearchesTags from '../components/start-page/recent-searches/recent-searches-tags';
import TrendingSearchesTags from '../components/start-page/trending/trending-searches-tags';
import PersonalisedTags from '../components/start-page/personalised/personalised-tags';
import { useIsPersonalisedBrandsTagsLoading } from '../components/start-page/personalised/personalised-brands-tags';
import {
    useIsPersonalisedSearchesTagsLoading,
    useShowPersonalisedSearchesFallback
} from '../components/start-page/personalised/personalised-searches-tags';

function StartPage({ pageType }) {
    const status = useSelector(selectStartPageStatus);
    const {
        isCampaignMode,
        displayRecentSearches,
        displayTrendingSearches
    } = useSelector(selectStartPageHeader);

    const dispatch = useDispatch();

    usePageMeta(pageType);
    useScrollToTopOnMount();

    const isDesktop = useIsDesktop();
    const isMobile = useIsMobile();
    const isLoggedIn = useIsLoggedIn();
    const isPersonalisedSuggestionsEnabled = useIsFeatureEnabled(
        'personalised-suggestions'
    );

    useEffect(() => {
        if (status === 'NOT_FETCHED') {
            dispatch(fetchStartPageConfig());
        }
    }, [dispatch, status]);

    const isPersonalisedBrandsTagsLoading = useIsPersonalisedBrandsTagsLoading();
    const isPersonalisedSearchesTagsLoading = useIsPersonalisedSearchesTagsLoading();
    const showPersonalisedSearchesFallback = useShowPersonalisedSearchesFallback();

    if (status !== 'HAS_DATA') {
        return (
            <div className="position-relative">
                <Loader usePortal={false} />
            </div>
        );
    }

    const showPersonalisedTags = isLoggedIn && isPersonalisedSuggestionsEnabled;

    const isLoadingPersonalisedTags =
        showPersonalisedTags &&
        (isPersonalisedBrandsTagsLoading || isPersonalisedSearchesTagsLoading);

    const moveTrendingSearchesToPersonalised =
        showPersonalisedTags && showPersonalisedSearchesFallback;

    const showTrendingSearchesInHeader =
        !isCampaignMode &&
        isDesktop &&
        !moveTrendingSearchesToPersonalised &&
        displayTrendingSearches;
    const showTrendingSearchesBelowHeader =
        !isCampaignMode &&
        !isDesktop &&
        !moveTrendingSearchesToPersonalised &&
        displayTrendingSearches;

    const showRecentSearchesInHeader =
        !isCampaignMode && !isMobile && displayRecentSearches;
    const showRecentSearchesBelowHeader =
        !isCampaignMode && isMobile && displayRecentSearches;

    return (
        <div
            className={`start-page position-relative ${
                isCampaignMode ? 'start-page--campaign-mode' : ''
            }`}>
            <ApplicationErrorBoundary>
                <StartPageHero
                    showRecentSearches={showRecentSearchesInHeader}
                    showTrendingSearches={showTrendingSearchesInHeader}
                    isLoading={isLoadingPersonalisedTags}
                />
            </ApplicationErrorBoundary>
            <div className="site-width position-relative">
                <div
                    style={{
                        opacity: isLoadingPersonalisedTags ? 0 : 1,
                        transition: 'opacity 200ms linear'
                    }}>
                    {showRecentSearchesBelowHeader && (
                        <section className="px-0 px-lg-2">
                            <RecentSearchesTags
                                inHeader={false}
                                className="border-bottom"
                            />
                        </section>
                    )}
                    {showTrendingSearchesBelowHeader && (
                        <section className="px-0 px-lg-2">
                            <TrendingSearchesTags
                                inHeader={false}
                                className="border-bottom"
                            />
                        </section>
                    )}
                    {showPersonalisedTags && (
                        <section>
                            <PersonalisedTags scroll={isMobile} />
                        </section>
                    )}
                </div>
                <PageContent />
                <Ad
                    adFormats="wide_skyscraper"
                    className="g-ad-startpage-side"
                    deviceName={'desktop-lg'}
                    position={'side-desktop-startpage-right'}
                    stickyBottom="#site-main"
                    stickyEnabled={true}
                    stickyTop={80}
                />
                <QuickView />
            </div>
        </div>
    );
}

StartPage.propTypes = {
    pageType: PropTypes.string.isRequired
};

export default StartPage;
